import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import travelCards from '../data/searchResults.json';

interface OfferGridProps {
    sectionSubtitle?: string;
    sectionTitle?: string;
    sectionAnnotation?: string;
    customStyle?: React.CSSProperties;
}

function toggleNavigation(swiper: SwiperClass) {
    const next = swiper?.navigation?.nextEl;
    const prev = swiper?.navigation?.prevEl;

    if (Number(swiper.slides.length) <= Number(swiper.params.slidesPerView)) {
        next.style.display = 'none';
        prev.style.display = 'none';
    } else {
        next.style.display = '';
        prev.style.display = '';
    }
}

const OfferGrid = ({
    sectionSubtitle = "Set Sail, Your Way",
    sectionTitle = "Tailored Yacht Charters",
    sectionAnnotation = "Flexible escapes that bring the freedom of the sea to you",
    customStyle = {}
}: OfferGridProps) => {
    const swiperRef = useRef<SwiperClass | null>(null);
    const [isCardsGridOpen, setIsCardsGridOpen] = useState(false);
    const sectionRef = useRef<HTMLDivElement | null>(null);

    const handleGridToggle = () => {
        setIsCardsGridOpen((prev) => {
            const next = !prev;
            if (prev && sectionRef.current) {
                setTimeout(() => {
                    sectionRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',

                    });
                }, 0);
            }
            return next;
        });
    }

    const renderCard = (card: typeof travelCards.results[0]) => (
        <Link className="card gopher" key={card.name} to="/search-result">
            <img src={card.images[0]} alt={card.name} loading='lazy' />
            <div className="card-content">
                <ul className="card-labels labels text-overflow">
                    {card.labels.map((label, index) => (
                        <li className={`${index !== card.location.length - 1 ? '' : 'text-overflow'}`} key={index}>{label}</li>
                    ))}
                </ul>
                <h3>{card.name}</h3>
                <div className="offer-box text-overflow">
                    <label className='text-overflow'>
                        {card.offerText}
                    </label>
                </div>
                <p className="price">{`From £${card.price.toLocaleString('en-GB')} p/week`}</p>
            </div>
        </Link>
    );

    return (
        <section className="travel-picks" id="offers" style={customStyle} ref={sectionRef}>
            <div className="container">
                <h3 className="section-subtitle">{sectionSubtitle}</h3>
                <h2 className="section-title">{sectionTitle}</h2>
                <h4 className="section-annotation">{sectionAnnotation}</h4>
                <div className="travel-picks desktop" >
                    <div className={`cards sale-cards ${isCardsGridOpen ? 'open' : ''}`}>
                        {travelCards.results.map(renderCard)}
                    </div>
                    <button
                        className="section-cta cta outline sale-toggle-btn"
                        onClick={handleGridToggle}
                        type="button"
                    >
                        {isCardsGridOpen ? 'Show less' : 'Show more'}
                    </button>
                </div>
                <div className="cards">
                    <Swiper
                        className="swiper sale-swiper travel-picks mobile"
                        modules={[Navigation]}
                        spaceBetween={24}
                        slidesPerView={'auto'}
                        navigation={{
                            nextEl: '.travel-picks-button-next',
                            prevEl: '.travel-picks-button-prev',
                        }}
                        breakpoints={{
                            320: {
                                spaceBetween: 16,
                            },
                            480: {
                                spaceBetween: 16,
                            },
                            768: {
                                spaceBetween: 16,
                            },
                            992: {
                                spaceBetween: 25,
                            },
                        }}
                        onAfterInit={(swiper: SwiperClass) => {
                            swiperRef.current = swiper;
                            toggleNavigation(swiper);
                        }}
                        onResize={(swiper: SwiperClass) => {
                            toggleNavigation(swiper);
                        }}
                    >
                        {travelCards.results.map((slide, idx) => (
                            <SwiperSlide key={idx}>
                                {renderCard(slide)}
                            </SwiperSlide>
                        ))}

                        <div className="swiper-button swiper-button-prev travel-picks-button-prev">
                            <svg width="10" height="18" viewBox="0 0 10 18">
                                <path d="M1 1L9 9L1 17" stroke="currentColor" />
                            </svg>
                        </div>
                        <div className="swiper-button swiper-button-next travel-picks-button-next">
                            <svg width="10" height="18" viewBox="0 0 10 18">
                                <path d="M1 1L9 9L1 17" stroke="currentColor" />
                            </svg>
                        </div>
                    </Swiper>
                </div>
            </div>
        </section>
    );
};

export default OfferGrid;
